import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Sparkles, Download, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api, { formatApiErrorDetail } from "@/lib/api";

const LeadCaptureModal = ({ open, onOpenChange }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [lines, setLines] = useState([]);
  const [interests, setInterests] = useState([]);

  useEffect(() => {
    if (open && lines.length === 0) {
      api.get("/taxonomy").then(({ data }) => setLines(data.lines)).catch(() => {});
    }
  }, [open, lines.length]);

  const toggleInterest = (id) =>
    setInterests((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/leads", { email, source: "modal", interests });
      setDone(true);
      toast.success(data.message || "¡Suscripción exitosa!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo suscribir.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { setDone(false); setEmail(""); } }}>
      <DialogContent data-testid="lead-modal" className="max-w-md border-emerald-500/30 bg-card overflow-hidden p-0">
        <DialogTitle className="sr-only">Guía de 30 Días para Desintoxicar tu Cuerpo y Hogar</DialogTitle>
        <DialogDescription className="sr-only">Descarga gratis nuestro protocolo de 30 días para eliminar toxinas.</DialogDescription>
        <div className="relative aura bg-secondary/40 p-8">
          <div className="relative z-10">
            <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 text-slate-950">
              <Sparkles className="h-6 w-6" />
            </div>
            {!done ? (
              <>
                <h2 className="font-display text-2xl font-extrabold tracking-tight">Guía de 30 Días para Desintoxicar tu Cuerpo y Hogar</h2>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  Descarga gratis nuestro protocolo paso a paso para eliminar toxinas de tu rutina, tu cocina y tu botiquín.
                </p>
                <form onSubmit={submit} className="mt-6 space-y-3">
                  <div className="relative">
                    <Mail className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
                    <input
                      data-testid="lead-email-input"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="tu@correo.com"
                      className="h-11 w-full rounded-lg border border-border bg-background pl-9 pr-3 text-sm outline-none focus:border-emerald-500/60"
                    />
                  </div>
                  {lines.length > 0 && (
                    <div data-testid="lead-interests">
                      <p className="text-xs text-muted-foreground mb-2">Me interesa recibir novedades sobre <span className="opacity-60">(opcional)</span>:</p>
                      <div className="flex flex-wrap gap-2">
                        {lines.map((l) => {
                          const active = interests.includes(l.id);
                          return (
                            <button
                              type="button"
                              key={l.id}
                              data-testid={`lead-interest-${l.id}`}
                              onClick={() => toggleInterest(l.id)}
                              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-all ${active ? "border-emerald-500 bg-emerald-500/20 text-emerald-300" : "border-border text-muted-foreground hover:border-emerald-500/50"}`}
                            >
                              {l.short}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <Button
                    data-testid="lead-submit-btn"
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-slate-950 font-bold h-11"
                  >
                    {loading ? "Enviando…" : "Quiero mi Guía Gratis"}
                  </Button>
                  <p className="text-center text-[11px] text-muted-foreground">Sin spam. Cancela cuando quieras.</p>
                </form>
              </>
            ) : (
              <div className="py-4 text-center">
                <h2 className="font-display text-2xl font-extrabold">¡Listo! 🎉</h2>
                <p className="mt-2 text-sm text-muted-foreground">Tu Guía de 30 Días está lista para descargar.</p>
                <a
                  data-testid="lead-download-btn"
                  href="/docs/guia-30-dias-detox.md"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500 px-6 py-3 text-sm font-bold text-slate-950"
                >
                  <Download className="h-4 w-4" /> Descargar Guía
                </a>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LeadCaptureModal;
